import { Controller } from '@hotwired/stimulus'

// Flowbite の Modal（data-modal-*）を置き換える素のダイアログ実装（issue #2203 Phase 2）。
// トリガーとパネルが別ファイルに分かれているケースがあるため、Stimulusのtargetsではなく
// data-modal-id-param（対象パネルのid）でdocument.getElementByIdを引く方式にしている。
// data-controller="modal" は app/views/layouts/fronts.haml / admin.html.haml の <body> に付与。
//
// 使い方:
//   トリガー: data-action="click->modal#show" data-modal-id-param="foo-modal"
//   閉じるボタン: data-action="click->modal#hide" data-modal-id-param="foo-modal"
//   トグル: data-action="click->modal#toggle" data-modal-id-param="foo-modal"
// data-modal-backdrop="static" が付いたパネルは背景クリックで閉じない（Flowbite挙動を踏襲）。
// aria-hidden/aria-modal/role も Flowbite 同様に開閉と連動して着脱する
// （スクリーンリーダーが視覚的に開いているモーダルを非表示のまま扱わないため）。
//
// モーダルの中から別のモーダルを開くネスト構成（例: _reviews_modal.html.haml 内の
// 画像プレビューモーダル）があるため、開いているパネルをスタックで管理する。単一の
// currentTarget にすると、内側を閉じた時に外側がまだ開いているのに背景オーバーレイと
// body の overflow-hidden を消してしまう不具合が起きるため。
//
// justify-center/items-center は静的なHAML側には無く、Flowbite本家もJSのshow()側で
// 動的に付与している（デフォルトplacement 'center'）。Flowbite 1.6.5の実装
// （node_modules/flowbite/lib/cjs/components/modal/index.js の initModals()）は
// data-modal-placement をトリガー要素ではなくパネル要素（$modalEl）からのみ読む。
// 本アプリのトリガー側に過去 data-modal-placement="bottom" が付いていた箇所が
// 複数あったが、パネル側には一度も設定されておらず元々no-opだった（実際の挙動は
// 常にデフォルトの'center'）。そのため center 固定で挙動は変わらない。
export default class extends Controller {
  connect() {
    this.stack = []
    this.boundKeydown = this.onKeydown.bind(this)
    document.addEventListener('keydown', this.boundKeydown)
  }

  disconnect() {
    document.removeEventListener('keydown', this.boundKeydown)
    this.removeBackdrop()
  }

  show(event) {
    const target = document.getElementById(event.params.id)
    if (!target || this.stack.includes(target)) return
    this.stack.push(target)
    target.classList.remove('hidden')
    target.classList.add('flex', 'justify-center', 'items-center')
    target.removeAttribute('aria-hidden')
    target.setAttribute('aria-modal', 'true')
    target.setAttribute('role', 'dialog')
    document.body.classList.add('overflow-hidden')
    this.showBackdrop()
  }

  hide(event) {
    this.close(document.getElementById(event.params.id))
  }

  toggle(event) {
    const target = document.getElementById(event.params.id)
    if (!target) return
    if (target.classList.contains('hidden')) {
      this.show(event)
    } else {
      this.close(target)
    }
  }

  close(target) {
    if (!target) return
    target.classList.add('hidden')
    target.classList.remove('flex')
    target.setAttribute('aria-hidden', 'true')
    target.removeAttribute('aria-modal')
    target.removeAttribute('role')
    this.stack = this.stack.filter((t) => t !== target)
    if (this.stack.length === 0) {
      document.body.classList.remove('overflow-hidden')
      this.removeBackdrop()
    }
  }

  top() {
    return this.stack[this.stack.length - 1]
  }

  onKeydown(event) {
    if (event.key === 'Escape' && this.top()) {
      this.close(this.top())
    }
  }

  showBackdrop() {
    if (this.backdropEl) return
    const backdrop = document.createElement('div')
    backdrop.className = 'fixed inset-0 z-30 bg-gray-900/50'
    backdrop.addEventListener('click', () => {
      const target = this.top()
      if (target && target.dataset.modalBackdrop !== 'static') {
        this.close(target)
      }
    })
    document.body.appendChild(backdrop)
    this.backdropEl = backdrop
  }

  removeBackdrop() {
    if (this.backdropEl) {
      this.backdropEl.remove()
      this.backdropEl = null
    }
  }
}
